import React, { useState, useEffect } from 'react';
import { Activity, Info } from 'lucide-react';
import { getFredRecessionScore } from '../../services/api';
import './RecessionAlert.css';

// riskLevel → 표시 정보
const RISK_LEVEL_MAP = {
  SAFE:   { label: '안전',     color: '#16a34a', bg: '#dcfce7', borderColor: '#86efac', desc: '경기 확장 국면이 지속되고 있습니다.' },
  WATCH:  { label: '주의',     color: '#ca8a04', bg: '#fef9c3', borderColor: '#fde047', desc: '일부 지표에서 위험 신호가 감지됩니다.' },
  ALERT:  { label: '경고',     color: '#ea580c', bg: '#ffedd5', borderColor: '#fdba74', desc: '복수의 지표에서 침체 선행 신호가 나타납니다.' },
  DANGER: { label: '침체 진입', color: '#dc2626', bg: '#fee2e2', borderColor: '#fca5a5', desc: '핵심 지표가 침체 진입을 가리키고 있습니다.' },
};

const resolveStage = (level, score) => {
  if (level && RISK_LEVEL_MAP[level]) return RISK_LEVEL_MAP[level];
  if (score >= 76) return RISK_LEVEL_MAP.DANGER;
  if (score >= 51) return RISK_LEVEL_MAP.ALERT;
  if (score >= 26) return RISK_LEVEL_MAP.WATCH;
  return RISK_LEVEL_MAP.SAFE;
};

// 시리즈별 단위/포맷
const PCT_SERIES = new Set(['T10Y2Y', 'UNRATE', 'A191RL1Q225SBEA', 'BAMLH0A0HYM2']);

const formatIndicatorValue = (ind) => {
  // CPI 등 yoyPercent 가공값 우선
  if (ind.calculatedValueLabel === 'yoyPercent' && ind.calculatedValue != null) {
    return { value: ind.calculatedValue.toFixed(2), unit: '%', tag: 'YoY' };
  }
  const v = ind.latestValue;
  if (ind.seriesId === 'IC4WSA' && typeof v === 'number') {
    return { value: (v / 10000).toFixed(1), unit: '만건', tag: '' };
  }
  if (ind.seriesId === 'HOUST' && typeof v === 'number') {
    return { value: v.toLocaleString(), unit: '천호', tag: '' };
  }
  if (ind.seriesId === 'SAHMREALTIME' && typeof v === 'number') {
    return { value: v.toFixed(2), unit: '', tag: '' };
  }
  if (typeof v === 'number') {
    if (PCT_SERIES.has(ind.seriesId)) return { value: v.toFixed(2), unit: '%', tag: '' };
    return { value: v.toFixed(2), unit: '', tag: '' };
  }
  return { value: String(v ?? '-'), unit: '', tag: '' };
};

const RecessionAlert = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await getFredRecessionScore();
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  if (loading || error || !data) {
    return null;
  }

  const { score = 0, maxScore = 100, riskLevel, indicators = [] } = data;
  const stage = resolveStage(riskLevel, score);

  const RADIUS = 44;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const progress = (score / maxScore) * CIRCUMFERENCE;

  // 최신 업데이트 일자 (indicator들 중 가장 늦은 날짜)
  const latestUpdated = indicators
    .map((i) => i.latestDate)
    .filter(Boolean)
    .sort()
    .reverse()[0] || '';

  return (
    <div className="feature-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="feature-icon ra-icon">
          <Activity size={20} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-black">경기 침체 위험 지수</h2>
            <span className="ra-beta-badge">베타</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">FRED 거시경제 9개 지표 가중합산</p>
        </div>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-4">
        장단기 금리, Sahm Rule 등 9개 지표를 종합해 침체 위험을 0~{maxScore}점으로 표시합니다. 점수가 높을수록 위험이 큽니다.
      </p>

      <div className="ra-score-card">
        <div className="ra-score-gauge">
          <svg width="108" height="108" viewBox="0 0 108 108">
            <circle cx="54" cy="54" r={RADIUS} fill="none" stroke="#f3f4f6" strokeWidth="10" />
            <circle
              cx="54" cy="54" r={RADIUS}
              fill="none"
              stroke={stage.color}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${progress} ${CIRCUMFERENCE}`}
              transform="rotate(-90 54 54)"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
            <text x="54" y="50" textAnchor="middle" className="ra-gauge-score" fill={stage.color}>
              {score}
            </text>
            <text x="54" y="64" textAnchor="middle" className="ra-gauge-label">
              / {maxScore}
            </text>
          </svg>
        </div>
        <div className="ra-score-info">
          <div
            className="ra-stage-badge"
            style={{ color: stage.color, background: stage.bg, borderColor: stage.borderColor }}
          >
            {stage.label}
          </div>
          <div className="ra-score-desc">{stage.desc}</div>
          {latestUpdated && <div className="ra-updated">기준일 {latestUpdated}</div>}
        </div>
      </div>

      <div className="ra-ind-grid">
        {indicators.map((ind) => {
          const isRisk = ind.status === 'RISK';
          const dotColor = isRisk ? '#ef4444' : '#22c55e';
          const statusLabel = isRisk ? '위험' : '양호';
          const { value, unit, tag } = formatIndicatorValue(ind);
          return (
            <div key={ind.seriesId} className="ra-ind-card">
              <div className="ra-ind-header">
                <span className="ra-ind-dot" style={{ background: dotColor }} />
                <span className="ra-ind-name">{ind.name}</span>
                <button
                  className="ra-ind-info-btn"
                  onMouseEnter={() => setActiveTooltip(ind.seriesId)}
                  onMouseLeave={() => setActiveTooltip(null)}
                  onTouchStart={() => setActiveTooltip(ind.seriesId)}
                  onTouchEnd={() => setActiveTooltip(null)}
                >
                  <Info size={9} className="text-gray-300 hover:text-gray-500" />
                  {activeTooltip === ind.seriesId && (
                    <div className="ra-ind-tooltip">
                      <div className="ra-ind-tooltip-name">{ind.name}</div>
                      <div className="ra-ind-tooltip-desc">{ind.signal}</div>
                      <div className="ra-ind-tooltip-meta">
                        가중치 {ind.weight}% · 기여 {ind.score}점
                        {ind.latestDate && ` · ${ind.latestDate}`}
                      </div>
                    </div>
                  )}
                </button>
              </div>
              <div className="ra-ind-body">
                <span className="ra-ind-value" style={{ color: dotColor }}>
                  {value}
                  {unit && <span className="ra-ind-unit">{unit}</span>}
                  {tag && <span className="ra-ind-tag">{tag}</span>}
                </span>
                <span className="ra-ind-status" style={{ color: dotColor }}>{statusLabel}</span>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default RecessionAlert;
